import React, { FunctionComponent, useEffect } from 'react';
import { useFieldArray, Controller } from 'react-hook-form';
import { Column, Row } from '@/components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleCheck, faEdit, faPlus, faTrashCan } from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-toastify';
import { useService } from '@/containers/ActionServiceV2/context';
import { Checkbox } from '@/components/Checkbox';
import { Input } from '@/components/Input';

const DEFAULT_STATE = { question: '', isSave: false, is_required: false };

export const AddiotionalQuestion: FunctionComponent = () => {
  const { editFormControl, watch } = useService();
  const { fields, append, remove, update } = useFieldArray({
    control: editFormControl,
    name: 'questions'
  });

  const questions = watch('questions');
  const [editionEnabled, setEditionEnabled] = React.useState(false);

  useEffect(() => {
    const isSave = questions.every((question) => question.isSave);
    setEditionEnabled(!isSave);
  }, [questions]);

  return (
    <Column gap={16} className="mb-8 mt-6 w-full" aria-label="Preguntas adicionales">
      {fields.length > 0 && (
        <Row align="center" className="mb-2">
          <h3 className="text-lg font-medium">Preguntas adicionales</h3>
        </Row>
      )}

      <Column gap={20}>
        {fields.map((item, index) => {
          const actualItems = questions[index];

          const handleSave = () => {
            if (actualItems.question.trim()) {
              update(index, { ...actualItems, isSave: true });
              setEditionEnabled(false);
            } else {
              toast.error('Por favor, ingrese una pregunta válida');
            }
          };

          const handleEdit = () => {
            if (!editionEnabled || !actualItems.isSave) {
              setEditionEnabled(true);
              update(index, { ...actualItems, isSave: false });
            }
          };

          const handleDelete = () => {
            remove(index);
            setEditionEnabled(questions.length > 1);
          };

          const handleRequired = () => {
            update(index, { ...actualItems, is_required: !actualItems.is_required });
          };

          return (
            <Column key={item.id} gap={12} className="border-b border-gray-100 pb-4 last:border-b-0">
              <Controller
                control={editFormControl}
                name={`questions.${index}.question`}
                render={({ field: { onChange, value } }) => (
                  <Input
                    inputId={`question-${index}`}
                    placeholder={`Pregunta ${index + 1}`}
                    onChange={onChange}
                    value={value || ''}
                    disabled={item.isSave}
                    aria-label={`Pregunta adicional ${index + 1}`}
                  />
                )}
              />
              <Row justify="space-between" align="center" className="mt-2">
                <Row gap={8} align="center">
                  <Checkbox
                    enable={item.is_required}
                    onClick={handleRequired}
                    aria-label={`Marcar pregunta ${index + 1} como obligatoria`}
                    disabled={item.isSave}
                  />
                  <label
                    htmlFor={`required-${index}`}
                    className="text-sm text-[#858585] font-medium cursor-pointer"
                  >
                    Obligatoria
                  </label>
                </Row>
                <Row gap={12}>
                  {actualItems.isSave ? (
                    <>
                      <button
                        onClick={handleEdit}
                        disabled={editionEnabled && actualItems.isSave}
                        className="p-1 text-[#858585] hover:text-blue disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        aria-label={`Editar pregunta ${index + 1}`}
                      >
                        <FontAwesomeIcon icon={faEdit} size="lg" />
                      </button>
                      {questions.length >= 1 && (
                        <button
                          onClick={handleDelete}
                          className="p-1 text-[#858585] hover:text-red-500 transition-colors"
                          aria-label={`Eliminar pregunta ${index + 1}`}
                        >
                          <FontAwesomeIcon icon={faTrashCan} size="lg" />
                        </button>
                      )}
                    </>
                  ) : (
                    <>
                      <button
                        onClick={handleSave}
                        className="p-1 text-blue hover:text-blue/80 transition-colors"
                        aria-label={`Guardar pregunta ${index + 1}`}
                      >
                        <FontAwesomeIcon icon={faCircleCheck} size="lg" />
                      </button>
                      {questions.length >= 1 && (
                        <button
                          onClick={handleDelete}
                          className="p-1 text-[#858585] hover:text-red-500 transition-colors"
                          aria-label={`Eliminar pregunta ${index + 1}`}
                        >
                          <FontAwesomeIcon icon={faTrashCan} size="lg" />
                        </button>
                      )}
                    </>
                  )}
                </Row>
              </Row>
            </Column>
          );
        })}
      </Column>

      {fields.length <= 4 && !editionEnabled && (
        <Row justify="center" className="mt-2">
          <button
            onClick={() => {
              append(DEFAULT_STATE);
              setEditionEnabled(true);
            }}
            className="flex items-center gap-2 px-4 py-2 bg-blue/10 text-blue hover:text-blue/80 font-medium rounded-md hover:bg-blue/20 transition-all"
            aria-label="Agregar nueva pregunta"
          >
            <FontAwesomeIcon icon={faPlus} size="sm" />
            Agregar pregunta
          </button>
        </Row>
      )}
    </Column>
  );
};
